import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IScheduleEssaySubQuestion } from 'src/app/interface/dto';

@Component({
  selector: 'app-score-adjustment-modal',
  templateUrl: './score-adjustment-modal.component.html',
  styleUrls: ['./score-adjustment-modal.component.scss'],
})
export class ScoreAdjustmentModalComponent implements OnInit {
  @Input() currentScore: number;
  @Input() maxScore: number;
  @Input() questionId: number;
  @Input() questionNo: number;
  @Input() examinationType: string;
  @Input() answerStatusString: string;
  @Input() scheduleEssaySubQuestions: IScheduleEssaySubQuestion[];
  @Input() hasessaysubquestions: boolean;

  adjustmentForm: FormGroup;
  totalSubQuestionScore: number = 0;

  constructor(public activeModal: NgbActiveModal, private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initializeForm();

    if (this.isSAQExamination()) {
      const initialStatus = this.answerStatusString?.toLowerCase() || '';
      this.markingStatus.setValue(initialStatus);

      this.markingStatus.valueChanges.subscribe((status) => {
        if (status === 'correct') {
          this.adjustedScore.setValue(this.maxScore);
        } else {
          this.adjustedScore.setValue(0);
        }
      });
    }

    // if sectionalized marking is enabled and we have section scores
    if (this.hasessaysubquestions && this.scheduleEssaySubQuestions?.length > 0) {
      this.populateEssaySubQuestionScores();
      this.calculateTotalScore();

      // watch for changes in section scores
      this.essaySubQuestionScores.valueChanges.subscribe(() => {
        this.calculateTotalScore();
      });
    }
  }

  populateEssaySubQuestionScores(): void {
    if (this.scheduleEssaySubQuestions?.length > 0) {
      this.scheduleEssaySubQuestions.forEach((sQuestion) => {
        this.addEssayQuestionScore(sQuestion);
      });
    }
  }

  addEssayQuestionScore(sEssaySubQuestion: IScheduleEssaySubQuestion): void {
    const essaySubQuestionGroup = this.fb.group({
      essaySubQuestionId: [sEssaySubQuestion.essaySubQuestionId],
      questionNoLabel: [sEssaySubQuestion.questionNoLabel],
      maxMarks: [sEssaySubQuestion.marks],
      currentScore: [sEssaySubQuestion.markEarned],
      newScore: [
        sEssaySubQuestion.markEarned, 
        [Validators.required, Validators.max(sEssaySubQuestion.marks)]
      ]
    });

    this.essaySubQuestionScores.push(essaySubQuestionGroup);
  }

  private calculateTotalScore(): void {
    if (this.hasessaysubquestions && this.essaySubQuestionScores) {
      this.totalSubQuestionScore = this.essaySubQuestionScores.controls.reduce(
        (sum, control) => sum + (control.get('newScore').value || 0),
        0
      );
    }
  }

  private initializeForm(): void {
    const formControls = {
      adjustmentReason: ['', [Validators.required]],
    };

    if (this.isSAQExamination()) {
      formControls['markingStatus'] = ['', [Validators.required]];
      formControls['adjustedScore'] = [this.currentScore];
    } else {
      formControls['adjustedScore'] = [
        0,
        [Validators.required, Validators.max(this.maxScore)],
      ];
    }
   // Add FormArray for essay sub-question scores
    if (this.hasessaysubquestions) {
      formControls['essaySubQuestionScores'] = this.fb.array([]);
    }
    this.adjustmentForm = this.fb.group(formControls);
  }

  private isSAQExamination(): boolean {
    return this.examinationType === 'SAQ';
  }

  get adjustedScore() {
    return this.adjustmentForm.get('adjustedScore');
  }
  get adjustmentReason() {
    return this.adjustmentForm.get('adjustmentReason');
  }
  get markingStatus() {
    return this.adjustmentForm.get('markingStatus');
  }
  get essaySubQuestionScores() {
    return this.adjustmentForm.get('essaySubQuestionScores') as FormArray;
  }

  submit(): void {
    if (this.adjustmentForm.valid) {
      if (
        this.hasessaysubquestions &&
        this.totalSubQuestionScore > this.maxScore
      ) {
       // Mark form as invalid if sub-question scores exceed max score
        this.essaySubQuestionScores.controls.forEach((control) => {
          control.get('newScore').markAsTouched();
        });
        return;
      }

       const data = {
        esqId: this.questionId,
        adjustmentReason: this.adjustmentForm.value.adjustmentReason,
        adjustedScore: this.hasessaysubquestions ? this.totalSubQuestionScore : this.adjustmentForm.value.adjustedScore,
      };

       // Handle essay sub-questions
      if (this.hasessaysubquestions) {
        // Map the updated sub-question with new scores
        data['scheduleEssaySubQuestions'] = this.adjustmentForm.value.essaySubQuestionScores.map(sQuestion => ({
          essaySubQuestionId: sQuestion.essaySubQuestionId,
          questionNoLabel: sQuestion.questionNoLabel,
          markEarned: sQuestion.newScore
        }));
      }
      this.activeModal.close(data);
    } else {
      this.markFormGroupTouched(this.adjustmentForm);
    }
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach((arrayControl) => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        });
      } else {
        control.markAsTouched();
      }
    });
  }


  dismiss(): void {
    this.activeModal.dismiss('cancel');
  }
}
